import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import { Box, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';

interface DeletionInfo {
  type: 'COMPANY' | 'STORE';
  companyName: string;
  storeName: string;
  storeCount: number;
  subscriptionReleased: boolean;
  alreadyRequested: boolean;
}

const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '操作に失敗しました。';
};

/**
 * アカウント削除リクエスト（管理者のみ）。feast-fe ADR 0016。
 * BE: GET/POST /stores/{storeId}/deletion-request。
 * 企業:店舗が 1:1 なら企業削除、1:n なら店舗削除のリクエストになる（BE が判定）。
 * 店舗にサブスクリプションがある場合は自動更新が停止（解除）される。
 */
const StoreDeletionSection: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const isManager = localStorage.getItem('role') === 'ROLE_MANAGER';

  const [info, setInfo] = useState<DeletionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [done, setDone] = useState<DeletionInfo | null>(null);

  const fetchInfo = useCallback(() => {
    if (!store_id) return;
    setLoading(true);
    createApiRequest<DeletionInfo>(`/stores/${store_id}/deletion-request`, { method: 'GET' })
      .then((data) => setInfo(data))
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setLoading(false));
  }, [store_id]);

  useEffect(() => {
    if (isManager) fetchInfo();
    else setLoading(false);
  }, [isManager, fetchInfo]);

  const submit = () => {
    if (!store_id) return;
    setError(null);
    setBusy(true);
    createApiRequest<DeletionInfo>(`/stores/${store_id}/deletion-request`, { method: 'POST' })
      .then((data) => setDone(data))
      .catch((e) => setError(extractMessage(e)))
      .finally(() => {
        setBusy(false);
        setConfirmOpen(false);
      });
  };

  // 削除は管理者のみ。
  if (!isManager) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const isCompany = info?.type === 'COMPANY';
  const scopeLabel = isCompany ? '企業（配下の全店舗を含む）の削除' : 'この店舗の削除';

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom color="error">
        アカウントの削除
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {done || info?.alreadyRequested ? (
        <Alert severity="success" icon={false}>
          {done
            ? `${done.type === 'COMPANY' ? '企業削除' : '店舗削除'}のリクエストを送信しました。`
            : '削除リクエスト送信済みです。'}
          この店舗は<strong>ロック</strong>され、操作できなくなりました。運営にて確認のうえ手続きを行います。
          {(done?.subscriptionReleased || info?.subscriptionReleased) && ' サブスクリプションの自動更新は停止（解除）しました。'}
          <br />
          取り消す場合は、<strong>一度ログアウトし、再ログイン後の店舗選択画面で「ロック解除」</strong>を行ってください。
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.main' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {isCompany
              ? '※ この店舗は企業で唯一の店舗のため、削除リクエストは「企業（全店舗）」の削除になります。'
              : `※ この店舗を削除します（企業や他の店舗${info ? `（全${info.storeCount}店舗）` : ''}は残ります）。`}
          </Typography>
          {info?.subscriptionReleased && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              ご契約中のサブスクリプションは、リクエスト時に自動更新が停止（解除）されます。
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            リクエスト送信後、<strong>この店舗はロックされ操作できなくなります</strong>。
            運営（FEAST株式会社）にて確認のうえ、一定の猶予期間を経てデータを削除します。
            取り消す場合は、再ログイン後の店舗選択画面で「ロック解除」を行ってください。
          </Typography>
          <Button variant="outlined" color="error" disabled={busy} onClick={() => setConfirmOpen(true)}>
            {scopeLabel}をリクエスト
          </Button>
        </Paper>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={submit}
        title="アカウント削除のリクエスト"
        message={
          (isCompany
            ? `「${info?.companyName ?? ''}」（企業・全店舗）の削除をリクエストします。`
            : `「${info?.storeName ?? ''}」（店舗）の削除をリクエストします。`) +
          'リクエスト後、この店舗はロックされ操作できなくなります（運営による解除まで）。サブスクリプションがある場合は自動更新を停止します。よろしいですか？'
        }
        confirmText="削除をリクエスト"
        confirmColor="error"
        closeColor="inherit"
      />
    </Box>
  );
};

export default StoreDeletionSection;
